import { Component, type ReactNode } from "react";
import { Button } from "./Button.tsx";

interface Props {
  children: ReactNode;
  /** Key to clear if corrupt persisted state is the likely cause. */
  storageKey?: string;
}

interface State {
  error: Error | null;
}

/**
 * Per-app boundary. A crash must show something actionable rather than a white
 * screen — and because these apps persist their state, it offers to clear the
 * saved data, which is the usual cause of an unrecoverable render loop.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: unknown) {
    console.error("[suite] unhandled render error", error, info);
  }

  private reset = () => {
    const { storageKey } = this.props;
    if (storageKey) {
      try {
        window.localStorage.removeItem(storageKey);
      } catch {
        /* ignore */
      }
    }
    window.location.reload();
  };

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-4 p-6">
        <h1 className="text-lg font-semibold">Something broke</h1>
        <p className="text-sm text-fg-muted">
          This ran entirely in your browser, so nothing was sent anywhere. Reloading
          usually fixes it; if not, clearing this app&apos;s saved data will.
        </p>
        <pre className="max-h-40 overflow-auto rounded-lg bg-surface-2 p-3 text-xs">
          {error.message}
        </pre>
        <div className="flex gap-2">
          <Button onClick={() => window.location.reload()}>Reload</Button>
          {this.props.storageKey ? (
            <Button variant="secondary" onClick={this.reset}>
              Clear saved data and reload
            </Button>
          ) : null}
        </div>
      </div>
    );
  }
}
